import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import TextEditorButton from 'flarum/common/components/TextEditorButton';
import CompletedModal from './components/CompletedModal';
import NominateModal from './components/NominateModal';

/**
 * Composer buttons that write the bot's reply triggers for the member.
 *
 * The bot polls Flarum for replies containing "/completed" or "/nominate"
 * (cogs/forum_completed.py, cogs/forum_nominate.py) - it has no inbound
 * listener, so a form cannot submit to it directly. These buttons instead
 * compose exactly the text a member would have typed and drop it into the
 * reply they are already writing. Nothing server-side changes, so a bug
 * here cannot break the completion pipeline; the worst case is a member
 * types the command by hand, which still works.
 *
 * Which button appears is decided by the thread, because the thread IS the
 * context: /completed only means something in a game thread (that is how
 * the bot knows which game), and /nominate only runs in Bot Commands.
 *
 * NOTE on extend(): Flarum 2.0 lazy-loads components as separate chunks,
 * so importing TextEditor directly gives undefined at init time. Passing
 * the module path as a string applies the extension when the chunk loads.
 */

// Per-event game thread tags, mirroring cogs/flarum_threads.py's _TAG_IDS.
const GAME_TAG_IDS = [9, 10, 11, 12];

// The single discussion where the bot listens for /nominate, matching the
// bot's FORUM_BOT_COMMANDS_DISCUSSION_ID default.
const BOT_COMMANDS_DISCUSSION_ID = 17;
function currentDiscussion() {
  var _app$composer$body$at, _app$composer;
  // The composer body carries the discussion for a reply. Guarded because
  // the same TextEditor is used for editing posts and starting new
  // discussions, where there is no discussion yet.
  return (_app$composer$body$at = (_app$composer = app.composer) == null || (_app$composer = _app$composer.body) == null || (_app$composer = _app$composer.attrs) == null ? void 0 : _app$composer.discussion) != null ? _app$composer$body$at : null;
}
function isGameThread(discussion) {
  var _discussion$tags;
  if (!discussion) return false;
  const tags = ((_discussion$tags = discussion.tags) == null ? void 0 : _discussion$tags.call(discussion)) || [];
  return tags.some(tag => tag && GAME_TAG_IDS.includes(Number(tag.id())));
}
function isBotCommandsThread(discussion) {
  return !!discussion && Number(discussion.id()) === BOT_COMMANDS_DISCUSSION_ID;
}
app.initializers.add('jrh-forum-commands', () => {
  extend('flarum/common/components/TextEditor', 'toolbarItems', function (items) {
    if (!app.session.user) return;
    const discussion = currentDiscussion();
    // `this` is the TextEditor; its composer attr owns the editor driver
    // that actually performs the insert.
    const insert = text => {
      var _this$attrs$composer;
      const editor = (_this$attrs$composer = this.attrs.composer) == null ? void 0 : _this$attrs$composer.editor;
      if (!editor) return;
      editor.insertAtCursor(text, false);
    };

    // Flarum's ItemList renders HIGHEST priority first. flarum/markdown
    // registers its whole toolbar at exactly 100, and on a tie ItemList
    // falls back to insertion order - markdown's initializer runs first,
    // so matching its number left us second. 110 clears it outright.
    //
    // Worth being first: these buttons are the reason most members open a
    // reply in a game thread or in Bot Commands at all. Formatting is
    // incidental there, so this should be the first thing under the cursor
    // rather than the last icon in a row of eight.
    const FIRST = 110;
    if (isGameThread(discussion)) {
      items.add('jrh-completed', m(TextEditorButton, {
        icon: "fas fa-trophy",
        className: "Button Button--icon jrh-CommandButton",
        onclick: () => app.modal.show(CompletedModal, {
          oninsert: insert
        })
      }, app.translator.trans('jrh-forum-commands.forum.completed.button')), FIRST);
    }
    if (isBotCommandsThread(discussion)) {
      items.add('jrh-nominate', m(TextEditorButton, {
        icon: "fas fa-plus-circle",
        className: "Button Button--icon jrh-CommandButton",
        onclick: () => app.modal.show(NominateModal, {
          oninsert: insert
        })
      }, app.translator.trans('jrh-forum-commands.forum.nominate.button')), FIRST);
    }
  });
});
export default {};